import {NoteEvent, PhraseGenerator} from "./phrase-generator";

/**
 * Duration-carrying silence. The general model of silence in a melody
 * timeline: a response bar is a whole-bar rest, and a future in-phrase rest
 * control (deferred in ADR 0002) would produce shorter rests within a
 * phrase bar's batch. Provenance does not matter at the event level: to a
 * consumer, a rest is a rest.
 */
interface RestEvent {
    kind: 'rest';
    /** Duration in beats. */
    duration: number;
}

/**
 * The melody stream is a uniform sequence of bars; every bar yields a batch
 * of MelodyEvents. Consumers schedule notes and ignore rests; both kinds
 * carry duration, so anything chaining time over the stream treats them
 * uniformly.
 */
type MelodyEvent = NoteEvent | RestEvent;

interface PhraseCycleConfig {
    beatsPerBar: number;
    /** Whole bars of melodic silence after each phrase; >= 0 (negatives clamp to 0 at read time). */
    responseBars: number;
}

/**
 * PhraseCycle - the stateful owner of the phrase/response-bar cycle
 * (ADR 0002): one phrase bar, then responseBars rest bars, repeat. The
 * cycle always starts (and restarts, after reset()) on a phrase bar.
 *
 * It presents the batch-source shape: one nextBatch() call per bar, one
 * bar's events per call. Phrase bars delegate to PhraseGenerator (which
 * stays stateless); rest bars yield a single whole-bar rest, built inline -
 * a rest-generator class would wrap one object literal.
 *
 * responseBars and beatsPerBar are live-read from the config on every
 * nextBatch() call, so changes apply at the next bar boundary. The only
 * state is the bar counter; PhraseCycle knows nothing about scheduling,
 * audio, or clicks.
 */
class PhraseCycle {

    private readonly config: PhraseCycleConfig;
    private readonly phraseGenerator: PhraseGenerator;
    private barCount = 0;

    constructor(config: PhraseCycleConfig, phraseGenerator: PhraseGenerator) {
        this.config = config;
        this.phraseGenerator = phraseGenerator;
    }

    /** The next bar's events: a phrase batch or a single whole-bar rest. */
    nextBatch(): MelodyEvent[] {
        const responseBars = Math.max(0, this.config.responseBars);
        const isPhraseBar = this.barCount % (responseBars + 1) === 0;
        this.barCount++;
        if (isPhraseBar) {
            return this.phraseGenerator.generate();
        }
        return [{kind: 'rest', duration: this.config.beatsPerBar}];
    }

    /** Restart the cycle: the next batch is a phrase bar. */
    reset() {
        this.barCount = 0;
    }
}

export {PhraseCycle, PhraseCycleConfig, MelodyEvent, RestEvent};
